import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  Paper,
  Divider,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { useStore } from "../../app/stores/store";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import FullPageLoader from "../../app/shared/common/FullPageLoader";
import MapLocationPicker from "../../app/shared/components/MapLocationPicker";

/* -------------------- Component -------------------- */
const AddCustomer = observer(() => {
  const { onboardingStore, toasterStore } = useStore();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCrmCustomerType, setSelectedCrmCustomerType] = useState("");

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    onboardingStore.getAllState();
    onboardingStore.getCrmOnboardCustomers();
  }, [onboardingStore]);

  const formik = useFormik({
    // validateOnChange: false,
    // validateOnBlur: true,
    // validateOnMount: false,
    initialValues: {
      crmId: "",
      ownerName: "",
      businessName: "",
      legalName: "",
      email: "",
      mobileNumber: "",
      alternateMobileNumber: "",
      panNumber: "",
      gstNumber: "",
      fullAddress: "",
      latitude: "",
      longitude: "",
      landmark: "",
      state: "",
      city: "",
      pincode: "",
    },
    validationSchema: Yup.object({
      ownerName: Yup.string().required("Owner Name is required"),
      businessName: Yup.string().required("Business Name is required"),
      legalName: Yup.string().required("Legal Name is required"),
      email: Yup.string().email("Invalid email format"),
      mobileNumber: Yup.string()
        .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
        .required("Mobile Number is required"),
      alternateMobileNumber: Yup.string().matches(
        /^[0-9]{10}$/,
        "Alternate mobile must be 10 digits",
      ),
      panNumber: Yup.string()
        .trim()
        .transform((value) => value?.toUpperCase())
        .matches(/^[A-Z]{5}[0-9]{4}[A-Z]$/, "Invalid PAN format"),
      gstNumber: Yup.string()
        .trim()
        .transform((value) => value?.toUpperCase())
        .matches(
          /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/,
          "Invalid GST format",
        ),
      fullAddress: Yup.string().required("Full Address is required"),
      latitude: Yup.string().required("Latitude is required"),
      longitude: Yup.string().required("Longitude is required"),
      state: Yup.string().required("State is required"),
      city: Yup.string().required("City is required"),
      pincode: Yup.string()
        .matches(/^[1-9][0-9]{5}$/, "Pincode must be 6 digits")
        .required("Pincode is required"),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const stateObj = onboardingStore.stateList?.data.find(
          (s) => s.name === values.state,
        );
        const cityObj = onboardingStore.cityList?.data.find(
          (c) => c.name === values.city,
        );

        const userStr = localStorage.getItem("user");
        const userData = userStr ? JSON.parse(userStr) : null;
        const accountManagerId =
          userData?.data?.admin?.id || userData?.data?.id || undefined;

        const payload = {
          crmId: values.crmId || undefined,
          name: values.ownerName,
          businessName: values.businessName,
          legalName: values.legalName,
          isSignupCashbackCredit: false,
          email: values.email || undefined,
          mobile: values.mobileNumber,
          secondaryMobile: values.alternateMobileNumber || undefined,
          panNumber: values.panNumber || undefined,
          gstNumber: values.gstNumber || undefined,
          lat: values.latitude,
          long: values.longitude,
          stateId: stateObj?.id,
          cityId: cityObj?.id,
          address: values.fullAddress,
          pinCode: values.pincode,
          landMark: values.landmark,
          isDeleted: false,
          isActive: true,
          accountManagerId: accountManagerId,
        };

        const response = await onboardingStore.addOrganisation(payload);
        toasterStore.success("Customer added successfully");

        if (response && response.status_code === 1) {
          const orgId = response.data.id;
          formik.resetForm();
          navigate(`/customer-onboard/upload-attachment/${orgId}`, {
            state: {
              customerTypeId: selectedCrmCustomerType
            }
          });
        }
      } catch (error: any) {
        const msg =
          error?.response?.data?.msg ||
          "Something went wrong. Please try again.";
        toasterStore.error(msg);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const fieldError = (name: keyof typeof formik.values) =>
    Boolean(formik.touched[name] && formik.errors[name]);

  const fieldHelper = (name: keyof typeof formik.values) =>
    formik.touched[name] ? (formik.errors[name] as string) : "";

  // Get regions
  const states = onboardingStore.stateList?.data || [];
  const cities = onboardingStore.cityList?.data || [];

  return (
    <Box>
      <FullPageLoader open={isSubmitting} message="Creating customer..." />
      <Box>
        <Paper
          elevation={0}
          sx={{
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(10px)",
            borderRadius: 3,
            mb: 3,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BusinessIcon />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={600}>
                Add Customer
              </Typography>
            </Box>
          </Box>
        </Paper>

        <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
          <Paper
            elevation={0}
            sx={{
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              borderRadius: 3,
              p: 2,
              mb: 3,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}
            >
              <PersonIcon />
              <Typography variant="h6" fontWeight={600} color="text.primary">
                Basic Organization Details
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ mb: 3 }}>
              <TextField
                select
                fullWidth
                label="Select Pre-approved Customer"
                value={formik.values.crmId}
                onChange={(e) => {
                  const selectedCrmId = e.target.value;
                  const selectedCustomer = onboardingStore.crmOnboardCustomers.find((c: any) => c.salesId === selectedCrmId);
                  if (selectedCustomer) {
                    formik.setValues({
                      ...formik.values,
                      crmId: selectedCustomer.salesId || "",
                      ownerName: selectedCustomer.contact?.name || selectedCustomer.dealName || "",
                      businessName: selectedCustomer.customer?.companyName || "",
                      legalName: selectedCustomer.customer?.companyName || "",
                      email: selectedCustomer.contact?.email || "",
                      mobileNumber: selectedCustomer.contact?.phone || "",
                      gstNumber: selectedCustomer.customer?.gst || "",
                      fullAddress: selectedCustomer.customer?.address || "",
                      pincode: selectedCustomer.customer?.pincode || "",
                    });
                    setSelectedCrmCustomerType(selectedCustomer.customer?.customerTypeId || "")
                  } else {
                    formik.setValues({
                      ...formik.values,
                      crmId: "",
                      ownerName: "",
                      businessName: "",
                      legalName: "",
                      email: "",
                      mobileNumber: "",
                      gstNumber: "",
                      fullAddress: "",
                      pincode: "",
                    });
                    setSelectedCrmCustomerType("");
                  }
                }}
              >
                <MenuItem value="">None</MenuItem>
                {onboardingStore.crmOnboardCustomers.map((c: any) => (
                  <MenuItem key={c.salesId} value={c.salesId}>
                    {c.dealName || c.contact?.name || "Unknown Deal"}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              {[
                { name: "ownerName", label: "Owner Name", required: true },
                {
                  name: "businessName",
                  label: "Business Name",
                  required: true,
                },
                { name: "legalName", label: "Legal Name", required: true },
                { name: "email", label: "Email", type: "email" },
                {
                  name: "mobileNumber",
                  label: "Mobile Number",
                  required: true,
                },
                {
                  name: "alternateMobileNumber",
                  label: "Alternate Mobile Number",
                },
                { name: "panNumber", label: "PAN Number" },
                { name: "gstNumber", label: "GST Number" },
              ].map((field) => (
                <Box
                  key={field.name}
                  sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
                >
                  <TextField
                    fullWidth
                    label={field.label}
                    type={field.type || "text"}
                    {...formik.getFieldProps(field.name)}
                    error={fieldError(field.name as any)}
                    helperText={fieldHelper(field.name as any)}
                    required={field.required}
                  />
                </Box>
              ))}
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)",
              borderRadius: 3,
              p: 2,
              mb: 3,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15)",
              },
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}
            >
              <LocationOnIcon />
              <Typography variant="h6" fontWeight={600} color="text.primary">
                Address Details
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              <Box sx={{ flex: "1 1 100%" }}>
                <TextField
                  fullWidth
                  label="Full Address"
                  multiline
                  rows={4}
                  {...formik.getFieldProps("fullAddress")}
                  error={fieldError("fullAddress")}
                  helperText={fieldHelper("fullAddress")}
                  required
                />
              </Box>

              <Box sx={{ flex: "1 1 100%" }}>
                <MapLocationPicker
                  latitude={formik.values.latitude}
                  longitude={formik.values.longitude}
                  onLatitudeChange={(value) =>
                    formik.setFieldValue("latitude", value)
                  }
                  onLongitudeChange={(value) =>
                    formik.setFieldValue("longitude", value)
                  }
                  label="Select Location"
                />
              </Box>

              <Box
                sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
              >
                <TextField
                  fullWidth
                  label="Landmark"
                  {...formik.getFieldProps("landmark")}
                  error={fieldError("landmark")}
                  helperText={fieldHelper("landmark")}
                />
              </Box>

              {/* State Select */}
              <Box
                sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
              >
                <TextField
                  select
                  fullWidth
                  label="State"
                  value={formik.values.state}
                  onChange={(e) => {
                    formik.setFieldValue("state", e.target.value);
                    formik.setFieldValue("city", "");
                    const stateObj = states.find(
                      (s) => s.name === e.target.value,
                    );
                    if (stateObj) {
                      onboardingStore.getAllCities(stateObj.id);
                    }
                  }}
                  error={fieldError("state")}
                  helperText={fieldHelper("state")}
                  required
                >
                  <MenuItem value="">Select State</MenuItem>
                  {states.map((state) => (
                    <MenuItem key={state.id} value={state.name}>
                      {state.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              {/* City Select */}
              <Box
                sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
              >
                <TextField
                  select
                  fullWidth
                  label="City"
                  name="city"
                  value={formik.values.city}
                  onChange={formik.handleChange}
                  error={fieldError("city")}
                  helperText={fieldHelper("city")}
                  required
                  disabled={!formik.values.state}
                >
                  <MenuItem value="">Select City</MenuItem>
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.name}>
                      {city.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box
                sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 12px)" } }}
              >
                <TextField
                  fullWidth
                  label="Pincode"
                  inputProps={{ maxLength: 6 }}
                  {...formik.getFieldProps("pincode")}
                  error={fieldError("pincode")}
                  helperText={fieldHelper("pincode")}
                  required
                />
              </Box>
            </Box>
          </Paper>

          {/* Buttons */}
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mb: 3 }}
          >
            <Button type="reset" variant="outlined" size="large">
              Reset
            </Button>

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={!(formik.isValid && formik.dirty) || isSubmitting}
            >
              {isSubmitting ? "Creating..." : "Create Customer"}
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
});

export default AddCustomer;
